/**
 * Utility functions for object manipulation
 */

/**
 * Safely gets a nested property value from an object using dot notation
 * @param obj - The object to access
 * @param path - The dot-notation path (e.g., "customer.firstName")
 * @returns The value at the path, or undefined if the path doesn't exist
 */
export function getNestedValue<T = any>(obj: any, path: string): T | undefined {
  if (!obj || !path) return undefined;

  return path.split(".").reduce((current, key) => {
    return current && typeof current === "object" ? current[key] : undefined;
  }, obj);
}

/**
 * Safely sets a nested property value in an object using dot notation
 * @param obj - The object to modify
 * @param path - The dot-notation path (e.g., "customer.firstName")
 * @param value - The value to set
 * @returns The modified object
 */
export function setNestedValue<T = any>(obj: any, path: string, value: T): any {
  if (!obj || !path) return obj;

  const keys = path.split(".");
  const lastKey = keys.pop()!;
  const target = keys.reduce((current, key) => {
    if (!(key in current) || typeof current[key] !== "object") {
      current[key] = {};
    }

    return current[key];
  }, obj);

  target[lastKey] = value;

  return obj;
}
